import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Home.css';
import Usrnav from '../../usercomponents/Usrnav/Usrnav';
import Usrsearch from '../../usercomponents/Usrsearch/Usrsearch';
import Footer from '../../usercomponents/Footer/Footer';
import config from '../../config/config';
import { FiShoppingCart, FiShoppingBag, FiFilter, FiDollarSign, FiX } from 'react-icons/fi';
import { PulseLoader } from 'react-spinners';

function getSimilarity(a, b) {
  if (!a || !b) return 0;
  a = a.toLowerCase();
  b = b.toLowerCase();

  let longer = a.length > b.length ? a : b;
  let shorter = a.length > b.length ? b : a;

  let longerLength = longer.length;
  if (longerLength === 0) return 1.0;

  return (longerLength - editDistance(longer, shorter)) / parseFloat(longerLength);
}

function editDistance(s1, s2) {
  s1 = s1.toLowerCase();
  s2 = s2.toLowerCase();

  const costs = Array(s2.length + 1).fill(0);

  for (let i = 0; i <= s1.length; i++) {
    let lastValue = i;
    for (let j = 0; j <= s2.length; j++) {
      if (i === 0)
        costs[j] = j;
      else if (j > 0) {
        let newValue = costs[j - 1];
        if (s1.charAt(i - 1) !== s2.charAt(j - 1))
          newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
        costs[j - 1] = lastValue;
        lastValue = newValue;
      }
    }
    if (i > 0)
      costs[s2.length] = lastValue;
  }
  return costs[s2.length];
}

function Home() {
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [loading, setLoading] = useState(true);
  const [cartProductIds, setCartProductIds] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchProducts() {
      try {
        const response = await axios.get(`${config.apiBaseUrl}/api/products`);
        setProducts(response.data);
      } catch (err) {
        console.error('Failed to fetch products', err);
      } finally {
        setLoading(false);
      }
    }

    async function fetchUserCart() {
      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        const res = await axios.get(`${config.apiBaseUrl}/api/users/cart`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const ids = res.data.map(item => item.productId);
        setCartProductIds(ids);
      } catch (error) {
        console.error('Error fetching cart:', error);
      }
    }

    fetchProducts();
    fetchUserCart();
  }, []);

  const filteredProducts = products
    .map(product => {
      const similarity = getSimilarity(searchTerm, product.productName);
      return { ...product, similarity };
    })
    .filter(product => {
      const matchesTags = Array.isArray(product.tags) &&
        product.tags.some(tag =>
          getSimilarity(tag, searchTerm) > 0.6 ||
          tag.toLowerCase().includes(searchTerm.toLowerCase())
        );

      const matchesCategories = Array.isArray(product.categories) &&
        product.categories.some(category =>
          getSimilarity(category, searchTerm) > 0.6 ||
          category.toLowerCase().includes(searchTerm.toLowerCase())
        );

      const matchesSearch =
        product.similarity > 0.6 ||
        product.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        matchesTags ||
        matchesCategories;

      const matchesMinPrice =
        minPrice === '' || product.pdprice.npr >= parseInt(minPrice);

      const matchesMaxPrice =
        maxPrice === '' || product.pdprice.npr <= parseInt(maxPrice);

      return matchesSearch && matchesMinPrice && matchesMaxPrice;
    })
    .sort((a, b) => b.similarity - a.similarity);

  const groupedProducts = {};

  filteredProducts.forEach(product => {
    const categories = product.categories && product.categories.length > 0 ? product.categories : ['Uncategorized'];
    categories.forEach(category => {
      if (!groupedProducts[category]) {
        groupedProducts[category] = [];
      }
  
      if (!groupedProducts[category].some(p => p._id === product._id)) {
        groupedProducts[category].push(product);
      }
    });
  });
    
  const handleAddToCart = async (product) => {
    const token = localStorage.getItem('token');

    if (!token) {
      alert('Please login to add items to your cart');
      navigate('/shreeohm/login');
      return;
    }

    if (cartProductIds.includes(product._id)) {
      alert('This product is already in your cart');
      return;
    }

    try {
      const response = await axios.post(
        `${config.apiBaseUrl}/api/users/addtocart`,
        {
          productId: product._id,
          quantity: 1
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      alert(response.data.message);
      setCartProductIds(prev => [...prev, product._id]);

    } catch (error) {
      const status = error.response?.status;
      const message = error.response?.data?.message || error.message;

      console.error('Error adding product to cart:', status, message);

      if (status === 403) {
        alert('Session expired, please login again');
        localStorage.removeItem('token');
        navigate('/shreeohm/login');
      } else {
        alert('Error adding product to cart');
      }
    }
  };

  const productSuggestions = products
    .map(p => p.productName)
    .filter(name => name.toLowerCase().includes(searchTerm.toLowerCase()))
    .slice(0, 5);

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const clearFilters = () => {
    setMinPrice('');
    setMaxPrice('');
    setSearchTerm('');
  };
  const sortedCategories = Object.keys(groupedProducts).sort((a, b) => {
    if (a.toLowerCase() === 'top selling products') return -1;
    if (b.toLowerCase() === 'top selling products') return 1;
    return 0;
  });

  return (
    <div className="home-container">
      {/* Sticky Navbar */}
      <div className="nav-wrapper">
        <Usrnav />
      </div>
  
      {/* Search Bar */}
      <div className="search-wrapper">
        <Usrsearch searchTerm={searchTerm} setSearchTerm={setSearchTerm} suggestions={productSuggestions}/>
      </div>
  
      {/* Main Content Area */}
      <div className="homemiddle">
        {/* Mobile Filter Toggle Button */}
        <button className="mobile-filter-toggle" onClick={toggleFilters}>
          <FiFilter /> {showFilters ? 'Hide Filters' : 'Show Filters'}
        </button>

        {/* Filter Sidebar */}
        <div className={`hmleft ${showFilters ? 'show-filters' : ''}`}>
          <div className="sticky-filter">
            <div className="hmfbox">
              <div className="filter-header">
                <h4><FiFilter /> Filters</h4>
                <button className="clear-filters" onClick={clearFilters}>Clear All</button>
              </div>
              <button className="mobile-close-btn" onClick={toggleFilters}>
        <FiX size={24} />
      </button>
              <div className="price-filter">
                <div className="price-input-group">
                  <label> Min Price</label>
                  <input
                    type="number"
                    value={minPrice}
                    onChange={(e) => setMinPrice(e.target.value)}
                    placeholder="e.g. 100"
                    className="price-input"
                    min="0"
                  />
                </div>
  
                <div className="price-input-group">
                  <label> Max Price</label>
                  <input
                    type="number"
                    value={maxPrice}
                    onChange={(e) => setMaxPrice(e.target.value)}
                    placeholder="e.g. 1000"
                    className="price-input"
                    min={minPrice || "0"}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
  
        {/* Products Display Area */}
        <div className="hmright">
          {loading ? (
            <div className="loading-container">
              <PulseLoader color="#4a6bff" size={15} />
              <p>Loading products...</p>
            </div>
          ) : Object.keys(groupedProducts).length === 0 ? (
            <div className="empty-state">
              <img src="/empty-cart.svg" alt="No products found" />
              <h3>No products found</h3>
              <p>Try adjusting your search or filters</p>
              <button className="clear-filters" onClick={clearFilters}>Clear Filters</button>
            </div>
          ) : (
            sortedCategories.map(category => (
              <div key={category} className="category-section">
                <h2 className="section-title">{category}</h2>
                <div className="product-grid">
                  {groupedProducts[category].map(product => (
                    <div key={product._id} className="product-card">
                      <div className="product-image-container">
                        <img
                          src={product.images?.[0]?.url || '/product-placeholder.jpg'}
                          alt={product.productName}
                          className="product-image"
                          onClick={() => navigate(`/buynow/${product._id}`, { state: { product } })}
                        />
                        {product.pdprice?.discount && (
                          <span className="discount-badge">-{product.pdprice.discount}%</span>
                        )}
                      </div>
                      <div className="product-info">
                        <h3 className="product-name" onClick={() => navigate(`/buynow/${product._id}`, { state: { product } })}>
                          {product.productName}
                        </h3>
                        <div className="product-price">
                          <span className="current-price">NPR {product.pdprice?.npr}</span>
                          {product.pdprice?.originalPrice && (
                            <span className="original-price">NPR {product.pdprice.originalPrice}</span>
                          )}
                        </div>
  
                        <div className="product-buttons">
                          <button
                            className="buy-now"
                            onClick={() => navigate(`/buynow/${product._id}`, { state: { product } })}
                          >
                            <FiShoppingBag /> Buy Now
                          </button>
  
                          <button
                            className={`add-to-cart ${cartProductIds.includes(product._id) ? 'in-cart' : ''}`}
                            onClick={() => handleAddToCart(product)}
                          >
                            <FiShoppingCart /> {cartProductIds.includes(product._id) ? 'Added' : 'Add to Cart'}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      <Footer/>
    </div>
  );
}

export default Home;
